/**
 * MCP Resource provider for connectors
 */

import type { Resource, ResourceTemplate } from "@modelcontextprotocol/sdk/types.js";
import type { SchemaCache } from "../cache/schema-cache.js";
import type { ConnectorMetadataApi } from "../api/connector-metadata.js";
import { createChildLogger } from "../utils/logger.js";

const resourceLogger = createChildLogger({ module: "connector-resource" });

/**
 * Context for connector resources
 */
export interface ConnectorResourceContext {
  cache: SchemaCache | null;
  api: ConnectorMetadataApi;
}

/**
 * Resource templates for connectors
 */
export const connectorResourceTemplates: ResourceTemplate[] = [
  {
    uriTemplate: "connector://{connectorId}",
    name: "Connector Details",
    description: "Get details about a specific Power Automate connector",
    mimeType: "application/json",
  },
  {
    uriTemplate: "connector://{connectorId}/operations",
    name: "Connector Operations",
    description: "List all operations (actions and triggers) for a connector",
    mimeType: "application/json",
  },
  {
    uriTemplate: "connector://{connectorId}/operations/{operationId}",
    name: "Operation Schema",
    description: "Get the full schema for a specific connector operation",
    mimeType: "application/json",
  },
];

/**
 * List available connector resources
 */
export async function listConnectorResources(
  context: ConnectorResourceContext
): Promise<Resource[]> {
  const resources: Resource[] = [];

  // Try to get connectors from cache
  if (context.cache) {
    const connectors = context.cache.listConnectors({ limit: 100 });

    for (const connector of connectors) {
      resources.push({
        uri: `connector://${connector.name}`,
        name: connector.displayName,
        description: connector.description ?? `${connector.tier} connector`,
        mimeType: "application/json",
      });
    }

    if (resources.length > 0) {
      resourceLogger.debug({ count: resources.length }, "Listed connectors from cache");
      return resources;
    }
  }

  // Fall back to API (limited to avoid large responses)
  try {
    const connectors = await context.api.listConnectors();
    const limited = connectors.slice(0, 50); // Limit for performance

    for (const connector of limited) {
      resources.push({
        uri: `connector://${connector.name}`,
        name: connector.properties.displayName,
        description: connector.properties.description ?? `${connector.properties.tier} connector`,
        mimeType: "application/json",
      });
    }

    resourceLogger.debug({ count: resources.length }, "Listed connectors from API");
  } catch (error) {
    resourceLogger.warn({ error }, "Failed to list connectors from API");
  }

  return resources;
}

/**
 * Read a connector resource
 */
export async function readConnectorResource(
  context: ConnectorResourceContext,
  uri: string
): Promise<{ contents: Array<{ uri: string; mimeType: string; text: string }> }> {
  resourceLogger.debug({ uri }, "Reading connector resource");

  // Parse URI: connector://{connectorId}[/operations[/{operationId}]]
  const match = uri.match(/^connector:\/\/([^/]+)(\/operations(\/(.+))?)?$/);

  if (!match) {
    throw new Error(`Invalid connector resource URI: ${uri}`);
  }

  const connectorId = match[1];
  const hasOperations = match[2] !== undefined;
  const operationId = match[4];

  if (!connectorId) {
    throw new Error(`Missing connector ID in URI: ${uri}`);
  }

  // Get specific operation schema
  if (operationId) {
    const schema = await getOperationSchema(context, connectorId, operationId);
    return {
      contents: [
        {
          uri,
          mimeType: "application/json",
          text: JSON.stringify(schema, null, 2),
        },
      ],
    };
  }

  // List operations
  if (hasOperations) {
    const operations = await getConnectorOperations(context, connectorId);
    return {
      contents: [
        {
          uri,
          mimeType: "application/json",
          text: JSON.stringify(operations, null, 2),
        },
      ],
    };
  }

  // Get connector details
  const connector = await getConnectorDetails(context, connectorId);
  return {
    contents: [
      {
        uri,
        mimeType: "application/json",
        text: JSON.stringify(connector, null, 2),
      },
    ],
  };
}

/**
 * Get connector details
 */
async function getConnectorDetails(
  context: ConnectorResourceContext,
  connectorId: string
): Promise<{
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  publisher: string | null;
  tier: string;
  isCustomApi: boolean;
  capabilities: string[] | null;
}> {
  // Try cache first
  if (context.cache) {
    const cached = context.cache.getConnector(connectorId);
    if (cached) {
      return {
        id: cached.id,
        name: cached.name,
        displayName: cached.displayName,
        description: cached.description,
        publisher: cached.publisher,
        tier: cached.tier,
        isCustomApi: cached.isCustomApi,
        capabilities: cached.capabilities,
      };
    }
  }

  // Fall back to API
  const connector = await context.api.getConnector(connectorId, false);
  return {
    id: connector.id,
    name: connector.name,
    displayName: connector.properties.displayName,
    description: connector.properties.description ?? null,
    publisher: connector.properties.publisher ?? null,
    tier: connector.properties.tier,
    isCustomApi: connector.properties.isCustomApi,
    capabilities: connector.properties.capabilities ?? null,
  };
}

/**
 * Get connector operations
 */
async function getConnectorOperations(
  context: ConnectorResourceContext,
  connectorId: string
): Promise<{
  connectorId: string;
  triggerCount: number;
  actionCount: number;
  triggers: Array<{
    operationId: string;
    summary: string | null;
    description: string | null;
  }>;
  actions: Array<{
    operationId: string;
    summary: string | null;
    description: string | null;
  }>;
}> {
  // Try cache first
  if (context.cache) {
    const operations = context.cache.getOperations(connectorId);
    if (operations.length > 0) {
      const triggers = operations.filter((o) => o.isTrigger).map((o) => ({
        operationId: o.operationId,
        summary: o.summary,
        description: o.description,
      }));
      const actions = operations.filter((o) => !o.isTrigger).map((o) => ({
        operationId: o.operationId,
        summary: o.summary,
        description: o.description,
      }));

      return {
        connectorId,
        triggerCount: triggers.length,
        actionCount: actions.length,
        triggers,
        actions,
      };
    }
  }

  // Fall back to API
  const apiOperations = await context.api.getConnectorActions(connectorId);
  const triggers = apiOperations.filter((o) => o.isTrigger).map((o) => ({
    operationId: o.operationId,
    summary: o.summary ?? null,
    description: o.description ?? null,
  }));
  const actions = apiOperations.filter((o) => !o.isTrigger).map((o) => ({
    operationId: o.operationId,
    summary: o.summary ?? null,
    description: o.description ?? null,
  }));

  return {
    connectorId,
    triggerCount: triggers.length,
    actionCount: actions.length,
    triggers,
    actions,
  };
}

/**
 * Get operation schema
 */
async function getOperationSchema(
  context: ConnectorResourceContext,
  connectorId: string,
  operationId: string
): Promise<{
  connectorId: string;
  operationId: string;
  method: string;
  path: string;
  summary: string | null;
  description: string | null;
  isTrigger: boolean;
  parameters: unknown[] | null;
  responses: Record<string, unknown> | null;
}> {
  // Try cache first
  if (context.cache) {
    const cached = context.cache.getOperation(connectorId, operationId);
    if (cached?.schema) {
      return {
        connectorId,
        operationId: cached.operationId,
        method: cached.method ?? "UNKNOWN",
        path: cached.path ?? "",
        summary: cached.summary,
        description: cached.description,
        isTrigger: cached.isTrigger,
        parameters: cached.schema.parameters ?? null,
        responses: cached.schema.responses ?? null,
      };
    }
  }

  // Fall back to API
  const schema = await context.api.getActionSchema(connectorId, operationId);
  if (!schema) {
    throw new Error(`Operation '${operationId}' not found in connector '${connectorId}'`);
  }

  return {
    connectorId,
    operationId: schema.operationId,
    method: schema.method,
    path: schema.path,
    summary: schema.summary ?? null,
    description: schema.description ?? null,
    isTrigger: false,
    parameters: schema.parameters ?? null,
    responses: schema.responses ?? null,
  };
}
